import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { catchError, map, of, switchMap } from "rxjs";

import { RoleService } from "@app/shared/services/roles.service";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { PHMS_SUBJECT_TERMINOLOGY } from "../phms-subject.terminology";
import { PHMS_OPERATIONAL_TERMINOLOGY } from "../phms-operational.terminology";
import { PhmsIntakeRuntimeService } from "../intake/phms-intake-runtime.service";
import {
    PhmsIntakeSubmissionDto,
    PhmsIntakeDisplayError,
    mapPhmsIntakeError,
} from "../intake/phms-manual-intake-submission.models";
import { PhmsManualIntakeSubmissionService } from "../intake/phms-manual-intake-submission.service";

const PHMS_INTAKE_SUBMIT = "phms.intake.submit";

@Component({
    selector: "phms-intake-submission-detail",
    standalone: true,
    imports: [CommonModule, UiLibraryModule, RouterLink],
    templateUrl: "./phms-intake-submission-detail.component.html",
    styleUrl: "./phms-intake-submission-detail.component.scss",
})
export class PhmsIntakeSubmissionDetailComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly route = inject(ActivatedRoute);
    private readonly roles = inject(RoleService);
    private readonly intakeService = inject(PhmsManualIntakeSubmissionService);
    private readonly runtime = inject(PhmsIntakeRuntimeService);
    readonly subjectText = createTerminologyUsageSignals(PHMS_SUBJECT_TERMINOLOGY.intakeDetail);
    readonly operationalText = createTerminologyUsageSignals(PHMS_OPERATIONAL_TERMINOLOGY.intakeDetail);

    readonly canRead = this.roles.hasAnyPermissionSignal(PHMS_INTAKE_SUBMIT);

    readonly isLoading = signal(true);
    readonly intakeSubmission = signal<PhmsIntakeSubmissionDto | null>(null);
    readonly errorState = signal<PhmsIntakeDisplayError | null>(null);

    readonly nameLabel = this.runtime.incarceratedPersonDisplayNameLabel;
    readonly identifierLabel = this.runtime.incarceratedPersonIdentifierLabel;
    readonly programScopeKeyLabel = this.runtime.programScopeKeyLabel;

    readonly pageTitle = "PHMS Intake Submission Detail";

    constructor() {
        this.runtime.loadFieldLabels().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        this.route.paramMap
            .pipe(
                map((params) => params.get("id")),
                switchMap((idValue) => {
                    this.errorState.set(null);
                    this.isLoading.set(true);

                    if (!idValue) {
                        this.errorState.set({
                            title: "Missing intake submission id",
                            messages: ["An intake submission id is required to load the detail view."],
                            issueCodes: [],
                        });
                        return of<PhmsIntakeSubmissionDto | null>(null);
                    }

                    const intakeSubmissionId = Number(idValue);
                    if (!Number.isFinite(intakeSubmissionId) || intakeSubmissionId <= 0) {
                        this.errorState.set({
                            title: "Invalid intake submission id",
                            messages: [`'${idValue}' is not a valid intake submission id.`],
                            issueCodes: [],
                        });
                        return of<PhmsIntakeSubmissionDto | null>(null);
                    }

                    return this.intakeService.getById(intakeSubmissionId).pipe(
                        catchError((error: unknown) => {
                            this.errorState.set(mapPhmsIntakeError(error));
                            return of<PhmsIntakeSubmissionDto | null>(null);
                        }),
                    );
                }),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((intakeSubmission) => {
                this.intakeSubmission.set(intakeSubmission);
                this.isLoading.set(false);
            });
    }
}

